import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/Toast";
import { API_BASE } from "../utils/api";

function PlanCard({ plan, current, onUpgrade, isLoading }) {
  const isCurrentPlan = current?.plan_type === plan.plan_type;

  return (
    <div
      className={`rounded-2xl border-2 overflow-hidden transition-all ${
        isCurrentPlan
          ? "border-luxury-gold bg-luxury-surface shadow-lg shadow-luxury-gold/20"
          : "border-luxury-border bg-luxury-surface hover:border-luxury-gold/50"
      }`}
    >
      {/* Current Badge */}
      {isCurrentPlan && (
        <div className="h-1 bg-luxury-gold" />
      )}

      <div className="p-6">
        {/* Plan Name */}
        <h3 className="text-xl font-bold text-luxury-text">{plan.name}</h3>
        <p className="text-sm text-luxury-secondary mt-1">{plan.description}</p>

        {/* Price */}
        <div className="mt-5 mb-6">
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold text-luxury-gold">
              R{plan.price?.toFixed(2) || "0.00"}
            </span>
            <span className="text-luxury-secondary text-sm">/month</span>
          </div>
        </div>

        {/* Features */}
        <ul className="space-y-3 mb-6">
          {plan.features?.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <svg className="w-5 h-5 text-luxury-cta flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm text-luxury-text">{feature}</span>
            </li>
          ))}
        </ul>

        {/* Button */}
        <button
          onClick={() => onUpgrade(plan)}
          disabled={isLoading || isCurrentPlan}
          className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
            isCurrentPlan
              ? "bg-luxury-elevated text-luxury-muted border border-luxury-border cursor-default"
              : isLoading
              ? "bg-luxury-elevated text-luxury-muted border border-luxury-border cursor-not-allowed"
              : "bg-luxury-cta hover:bg-luxury-cta-hover text-white active:scale-95"
          }`}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Processing...
            </span>
          ) : isCurrentPlan ? (
            "Current Plan"
          ) : (
            "Upgrade to " + plan.name
          )}
        </button>
      </div>
    </div>
  );
}

export default function Subscription() {
  const { token } = useAuth();
  const { addToast } = useToast();

  const [currentPlan, setCurrentPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    fetchSubscriptionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchSubscriptionData = async () => {
    setLoading(true);
    try {
      // Fetch current subscription
      const subRes = await fetch(`${API_BASE}/api/payments/subscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (subRes.ok) {
        const subData = await subRes.json();
        setCurrentPlan(subData);
      }

      // Fetch available plans
      const plansRes = await fetch(`${API_BASE}/api/payments/plans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (plansRes.ok) {
        const plansData = await plansRes.json();
        setPlans(Array.isArray(plansData) ? plansData : plansData.plans || []);
      }
    } catch (err) {
      addToast("Failed to load subscription data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (plan) => {
    if (upgrading) return;
    setUpgrading(true);

    try {
      const res = await fetch(`${API_BASE}/api/payments/initialize`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan_id: plan.id || plan.plan_type,
          amount: plan.price,
        }),
      });

      const data = await res.json();

      if (res.ok && data.authorization_url) {
        // Open PayStack in new window
        window.open(data.authorization_url, "_blank");
        addToast("Redirecting to payment gateway...", "success");
      } else {
        throw new Error(data.detail || "Failed to initialize payment");
      }
    } catch (err) {
      addToast(err.message || "Payment initialization failed", "error");
    } finally {
      setUpgrading(false);
    }
  };

  return (
    <div className="min-h-screen bg-luxury-bg p-6 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="rounded-2xl border border-luxury-border bg-luxury-surface p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-luxury-text">Manage Subscription</h1>
              <p className="text-luxury-secondary mt-2">
                Choose the perfect plan for your tax management needs
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-luxury-elevated border border-luxury-gold/30 flex-shrink-0">
              <svg className="w-6 h-6 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Current Plan Info */}
        {currentPlan && (
          <div className="rounded-2xl border border-luxury-cta/30 bg-luxury-cta/5 p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-luxury-secondary">Current Plan</p>
                <h2 className="text-2xl font-bold text-luxury-text mt-1">
                  {currentPlan.plan_name || currentPlan.plan_type}
                </h2>
                {currentPlan.renewal_date && (
                  <p className="text-sm text-luxury-secondary mt-2">
                    Renews on{" "}
                    {new Date(currentPlan.renewal_date).toLocaleDateString("en-ZA", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-luxury-gold">
                  R{currentPlan.amount?.toFixed(2) || "0.00"}
                </p>
                <span className="inline-flex items-center gap-2 mt-2 px-3 py-1 rounded-full bg-luxury-cta/20 text-luxury-cta text-xs font-semibold">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Active
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Plans Grid */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-48 rounded-2xl bg-luxury-surface border border-luxury-border animate-pulse"
              />
            ))}
          </div>
        ) : plans.length === 0 ? (
          <div className="rounded-2xl border border-luxury-border bg-luxury-surface p-12 text-center">
            <svg className="w-16 h-16 text-luxury-muted mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-lg font-semibold text-luxury-text">No plans available</p>
            <p className="text-sm text-luxury-secondary mt-1">Please contact support</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <PlanCard
                key={plan.id || plan.plan_type}
                plan={plan}
                current={currentPlan}
                onUpgrade={handleUpgrade}
                isLoading={upgrading}
              />
            ))}
          </div>
        )}

        {/* FAQ / Help Section */}
        <div className="rounded-2xl border border-luxury-border bg-luxury-surface p-8">
          <h3 className="text-xl font-bold text-luxury-text mb-6">Subscription FAQ</h3>

          <div className="space-y-5">
            {[
              {
                q: "Can I change my plan anytime?",
                a: "Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.",
              },
              {
                q: "What payment methods do you accept?",
                a: "We accept all major credit and debit cards via our secure PayStack payment gateway.",
              },
              {
                q: "Is there a free trial?",
                a: "Yes, all new users get a 14-day free trial to explore premium features risk-free.",
              },
              {
                q: "What happens if I cancel?",
                a: "Your account remains active until the end of your billing cycle. You'll lose access to premium features after that.",
              },
              {
                q: "Do you offer annual billing?",
                a: "Yes! Annual subscriptions come with 2 months free compared to monthly billing.",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="flex gap-4 pb-5 border-b border-luxury-border last:border-0"
              >
                <div className="w-8 h-8 rounded-full bg-luxury-elevated flex items-center justify-center flex-shrink-0 text-luxury-gold font-semibold text-sm">
                  Q
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-luxury-text mb-2">{item.q}</p>
                  <p className="text-sm text-luxury-secondary">{item.a}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-4 bg-luxury-elevated rounded-xl">
            <p className="text-sm text-luxury-secondary">
              Need help?{" "}
              <a href="mailto:support@taxvault.live" className="text-luxury-gold font-semibold hover:underline">
                Contact our support team
              </a>
            </p>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="rounded-2xl border border-luxury-border bg-luxury-surface p-6">
          <p className="text-sm text-luxury-secondary text-center mb-4">Secure & Trusted</p>
          <div className="flex items-center justify-center gap-6 flex-wrap">
            {[
              { icon: "🔒", label: "SSL Encrypted" },
              { icon: "✓", label: "PCI Compliant" },
              { icon: "🛡️", label: "Bank-Level Security" },
              { icon: "✓", label: "SARS Approved" },
            ].map((badge, idx) => (
              <div key={idx} className="text-center">
                <div className="text-2xl mb-1">{badge.icon}</div>
                <p className="text-xs text-luxury-secondary">{badge.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
